"use strict";
import {
    ServerConfig,
    initialize,
    OctaviusRocksPublic,
    buildHooks,
    collectEntity,
    PublicAPI,
    Hooks
} from '@octavius-rocks/api'

import {CollectEntity} from '@octavius-rocks/api/dist/utils/selectors'

declare global {
    interface Window {
        WP_OctaviusRocks: {
            config: ServerConfig
            pageviewEntity: Record<string, string>
            autoPageview:boolean
            autoClick: boolean
            autoRendered: boolean
            collectEntity: CollectEntity
            api: PublicAPI
            HOOK: unknown
            hooks: Hooks
            pageview: (customEntity?: Record<string, string>) => Promise<void>
            init: () => void
        };
    }
}

((oc) => {

    // ---------------------------------
    // public interaction object
    // ---------------------------------
    const {
        config,
        pageviewEntity,
        autoPageview = true,
        autoClick = false,
        autoRendered = false,
    } = oc;

    const api = oc.api = OctaviusRocksPublic(config);
    oc.collectEntity = collectEntity;

    const HOOK = oc.HOOK = {
        READY: "ready",
        PAGEVIEW: {
            ENTITY_ALTER: "pageview_entity_alter",
        },
        CLICK_TRACKER: {
            ENTITY_ALTER: "click_tracker_entity_alter",
            SELECTORS_ALTER: 'click_tracker_selectors_alter',
        },
        RENDERED_TRACKER: {
            ENTITY_ALTER: "rendered_tracker_entity_alter",
            SELECTORS_ALTER: 'rendered_tracker_selectors_alter',
        }
    }
    const hooks = oc.hooks = buildHooks();

    // ------------------------------------------------
    // send pageview
    // ------------------------------------------------
    oc.pageview = async (customEntity = {}) => {
        const elementEntity = collectEntity(globalThis.body)
        delete elementEntity.parent_url // only for sub elements
        const entity = {
            ...pageviewEntity,
            ...elementEntity,
            ...customEntity,
        };
        hooks.execute(HOOK.PAGEVIEW.ENTITY_ALTER, entity);
        await api.send().pageview(entity);
    }

    // ------------------------------------------------
    // automatically track clicks
    // ------------------------------------------------
    const ClickTracker = () => {
        const onClick = async (e) => {
            e.currentTarget.removeEventListener('click', onClick)
            const entity = collectEntity(e.currentTarget)
            hooks.execute(HOOK.CLICK_TRACKER.ENTITY_ALTER, entity)
            await api.send().sendClick(entity, {
                resting_time_average: api.send().restingTime(),
            });
        }
        const selectors = {
            target: 'a[href], button, input[type=submit]'
        }
        hooks.execute(
            HOOK.CLICK_TRACKER.SELECTORS_ALTER,
            selectors
        );
        const links = document.querySelectorAll(selectors.target);
        for (let i = 0; i < links.length; i++) {
            links[i].addEventListener('click', onClick);
        }
    }

    // ------------------------------------------------
    // automatically track rendered elements
    // ------------------------------------------------
    const RenderedTracker = async () => {

        const selectors = {
            target: "a:not(.oc-ignore-rendered)[href][data-octavius-content_id]",
            attribute_rendered: "data-oc-rendered",
        };
        hooks.execute(
            HOOK.RENDERED_TRACKER.SELECTORS_ALTER,
            selectors
        );

        function get_rendered_entity(element) {
            if (element.getAttribute(selectors.attribute_rendered)) return false;
            element.setAttribute(selectors.attribute_rendered, true);
            return collectEntity(element);
        }

        let listening_offset = 1

        async function waiting_for_elements() {
            //send render event for all elements
            const elements = document.querySelectorAll(selectors.target);
            const entities = [];
            for (let i = 0; i < elements.length; i++) {
                const entity = get_rendered_entity(elements[i]);
                if (false !== entity) entities.push(entity);
            }
            if (entities.length > 0) {
                await api.send().sendRendered(entities)
            }
            setTimeout(waiting_for_elements, 600 * listening_offset++);
        }

        await waiting_for_elements()
    }

    // ---------------------------------
    // after all scripts could hook their way in
    // ---------------------------------
    async function ready() {
        await initialize(config);
        hooks.execute(HOOK.READY, oc)
        if (autoPageview) {
            await oc.pageview();
        }
        if (autoClick) {
            ClickTracker()
        }
        if (autoRendered) {
            await RenderedTracker()
        }
    }

    // -----------------------------------------------------
    // expose init to public so very last script can call it
    // -----------------------------------------------------


    oc.init = () => {
        if (
            document.readyState === "complete"
        ) {
            ready()
        } else if (document.addEventListener) {
            document.addEventListener('DOMContentLoaded', ready)
        }
    }

})(window.WP_OctaviusRocks);